import { useMemo } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';

/**
 * StoreHoursBanner — thin banner that warns a consumer when the retailer is
 * closing soon (default: within 30 minutes) or already closed.
 *
 * Reads a retailer's ``store_hours`` string (e.g. "9 AM - 8 PM") or a
 * ``weekly_hours`` object (day → "open-close"). Purely presentational — no
 * API calls, no enforcement.
 *
 * Cluster 6 P4.
 */
export default function StoreHoursBanner({ retailer, warnMinutes = 30 }) {
  const info = useMemo(() => computeClosingInfo(retailer), [retailer]);
  if (!info) return null;

  const { minutesToClose, closesAtLabel, dayHours } = info;

  if (minutesToClose == null) return null;

  if (minutesToClose < 0) {
    return (
      <div
        data-testid="store-hours-banner-closed"
        className="flex items-center gap-2 rounded-md bg-slate-100 border border-slate-200 px-3 py-2 text-sm text-slate-700"
      >
        <Clock className="w-4 h-4" />
        <span>
          <b>Closed now.</b> Today's hours: {dayHours || 'unavailable'}. Orders will be accepted when the store reopens.
        </span>
      </div>
    );
  }

  if (minutesToClose <= warnMinutes) {
    return (
      <div
        data-testid="store-hours-banner-closing-soon"
        className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"
      >
        <AlertTriangle className="w-4 h-4" />
        <span>
          <b>Closing at {closesAtLabel}</b> — please place your order in the next {minutesToClose} minute{minutesToClose === 1 ? '' : 's'}.
        </span>
      </div>
    );
  }

  // Open with plenty of time — return null so we don't clutter the UI.
  return null;
}

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

function computeClosingInfo(retailer) {
  if (!retailer) return null;
  const now = new Date();
  const dayIdx = now.getDay(); // 0 = Sun
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayKey = days[dayIdx];

  // Prefer weekly_hours (object) if available
  let dayHours =
    (retailer.weekly_hours && retailer.weekly_hours[dayKey]) ||
    (retailer.store_hours && retailer.store_hours[dayKey]) ||  // some seeds shape hours nested
    (typeof retailer.store_hours === 'string' ? retailer.store_hours : null);

  if (!dayHours || typeof dayHours !== 'string') return null;
  if (dayHours.trim().toLowerCase() === 'closed') {
    return { minutesToClose: -1, closesAtLabel: null, dayHours: 'closed' };
  }

  const parts = dayHours.split(/–|-/); // allow both hyphen types
  if (parts.length < 2) return null;
  const openStr = parts[0].trim();
  const closeStr = parts[1].trim();

  const open = parseTime(openStr, now);
  const close = parseTime(closeStr, now);
  if (!open || !close) return null;

  if (now < open) return { minutesToClose: -1, closesAtLabel: closeStr, dayHours };
  if (now >= close) return { minutesToClose: -1, closesAtLabel: closeStr, dayHours };

  const minutes = Math.max(0, Math.round((close.getTime() - now.getTime()) / 60000));
  return {
    minutesToClose: minutes,
    closesAtLabel: closeStr,
    dayHours,
  };
}

function parseTime(hhmmStr, sameDay) {
  // Accepts "9 AM", "9:30 AM", "9:00 AM", "21:00", "9 pm"
  const m = String(hhmmStr).trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|AM|PM)?$/);
  if (!m) return null;
  let hour = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const ampm = m[3] ? m[3].toLowerCase() : null;
  if (ampm === 'pm' && hour < 12) hour += 12;
  if (ampm === 'am' && hour === 12) hour = 0;
  if (hour > 23 || min > 59) return null;
  const d = new Date(sameDay);
  d.setHours(hour, min, 0, 0);
  return d;
}
